import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Address } from '../types';
import { db, isFirebaseActive, removeUndefined, getFirebaseAuth } from '../firebase';
import { ref, get as dbGet, child, set as dbSet } from 'firebase/database';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';

const sanitizeLocalEmail = (email: string) => email.toLowerCase().replace(/\./g, '_');

const seedUsers: User[] = [
  {
    id: 'sensorzonetronic@gmail.com',
    name: 'Admin User',
    email: 'sensorzonetronic@gmail.com',
    phone: '0771234567',
    password: 'password123',
    createdAt: new Date('2026-05-01T10:00:00.000Z').toISOString(),
    addresses: [
      {
        id: 'addr-1',
        label: 'Home',
        street: '123 Main Street',
        city: 'Colombo 03',
        province: 'Western',
        phone: '0771234567'
      }
    ]
  },
  {
    id: 'tester@sensorzone.lk',
    name: 'Demo Tester',
    email: 'tester@sensorzone.lk',
    phone: '0779876543',
    password: 'testpassword',
    createdAt: new Date('2026-05-02T12:00:00.000Z').toISOString(),
    addresses: [
      {
        id: 'addr-2',
        label: 'Lab',
        street: '456 Tech Park',
        city: 'Matara',
        province: 'Southern',
        phone: '0779876543'
      }
    ]
  }
];

interface UserState {
  currentUser: User | null;
  registeredUsers: User[];
  
  // Actions
  registerUser: (user: Omit<User, 'id' | 'addresses' | 'createdAt'> & { street?: string; city?: string; province?: string }) => Promise<{ success: boolean; error?: string }>;
  loginUser: (email: string, password?: string) => Promise<{ success: boolean; error?: string }>;
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  logoutUser: () => void;
  updateProfile: (name: string, phone: string, password?: string) => Promise<{ success: boolean; error?: string }>;
  addAddress: (address: Omit<Address, 'id'>) => Promise<void>;
  updateAddress: (addressId: string, address: Partial<Address>) => Promise<void>;
  deleteAddress: (addressId: string) => Promise<void>;
  deleteAccount: () => Promise<{ success: boolean; error?: string }>;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      currentUser: null,
      registeredUsers: seedUsers,


      registerUser: async (userData) => {
        const sanitizedEmail = sanitizeLocalEmail(userData.email);
        const { street, city, province, ...coreData } = userData as any;

        // Build initial address if address details were provided
        const initialAddresses: Address[] = (street && city) ? [{
          id: 'addr-' + Math.floor(1000 + Math.random() * 9000),
          label: 'Home',
          street,
          city,
          province: province || 'Western',
          phone: coreData.phone
        }] : [];

        // Always check local store first for duplicates
        const localUsers = get().registeredUsers;
        const localExists = localUsers.some(u => u?.email && u.email.toLowerCase() === coreData.email.toLowerCase());
        if (localExists) {
          return { success: false, error: 'This email is already registered. Please sign in instead.' };
        }

        const newUser: User = {
          id: coreData.email,
          ...coreData,
          addresses: initialAddresses,
          createdAt: new Date().toISOString()
        };

        // Also check Firebase DB if available (to catch accounts registered on other devices)
        if (isFirebaseActive && db) {
          try {
            const userSnap = await dbGet(child(ref(db), `users/${sanitizedEmail}`));
            if (userSnap.exists()) {
              return { success: false, error: 'This email is already registered. Please sign in instead.' };
            }
          } catch {
            // If we can't read Firebase (permissions), just proceed with local registration
          }
        }

        // Save to local store immediately (always works)
        set({ registeredUsers: [...localUsers, newUser] });

        // Try to save to Firebase DB in background (may fail if rules block unauthenticated writes)
        if (isFirebaseActive && db) {
          try {
            await dbSet(ref(db, `users/${sanitizedEmail}`), removeUndefined(newUser));
          } catch (firebaseError: any) {
            console.warn('Firebase DB write failed (permission denied) — user saved locally only:', firebaseError.message);
            // User is still registered locally — no failure returned
          }
        }

        return { success: true };
      },

      loginUser: async (email, password) => {
        const sanitizedEmail = sanitizeLocalEmail(email);

        // First try local store (works even if Firebase is blocked)
        const localUsers = get().registeredUsers;
        const localUser = localUsers.find(u => u?.email && u.email.toLowerCase() === email.toLowerCase());

        // Try Firebase DB read if available
        let dbUser: User | null = null;
        if (isFirebaseActive && db) {
          try {
            const userSnap = await dbGet(child(ref(db), `users/${sanitizedEmail}`));
            if (userSnap.exists()) {
              dbUser = userSnap.val() as User;
              if (dbUser && dbUser.addresses) {
                dbUser.addresses = (Array.isArray(dbUser.addresses) ? dbUser.addresses : Object.values(dbUser.addresses)).filter(Boolean) as Address[];
              }
            }
          } catch {
            // Firebase read blocked — fall back to local store
          }
        }

        // Use DB user if found, else local user
        const user = dbUser || localUser || null;

        if (!user) {
          return { success: false, error: 'No account found with this email. Please register first.' };
        }

        // Validate password against stored password
        if (password && user.password !== password) {
          return { success: false, error: 'Incorrect password. Please try again.' };
        }

        set({ currentUser: user });
        return { success: true };
      },

      loginWithGoogle: async () => {
        const auth = getFirebaseAuth();
        if (isFirebaseActive && auth && db) {
          try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            const firebaseUser = result.user;
            
            if (!firebaseUser.email) {
              return { success: false, error: 'Could not retrieve email from Google account.' };
            }
            
            const sanitizedEmail = sanitizeLocalEmail(firebaseUser.email);
            
            // Check if the user already exists in the database
            const userSnap = await dbGet(child(ref(db), `users/${sanitizedEmail}`));
            
            let user: User;
            if (userSnap.exists()) {
              user = userSnap.val() as User;
              if (!user.email) {
                user.email = firebaseUser.email;
              }
              if (user && user.addresses) {
                user.addresses = (Array.isArray(user.addresses) ? user.addresses : Object.values(user.addresses)).filter(Boolean) as Address[];
              }
            } else {
              // Create a new user profile in DB
              user = {
                id: firebaseUser.email,
                name: firebaseUser.displayName || 'Google User',
                email: firebaseUser.email,
                phone: firebaseUser.phoneNumber || '',
                password: '', // No local password for Google accounts
                createdAt: new Date().toISOString(),
                addresses: []
              };
              await dbSet(ref(db, `users/${sanitizedEmail}`), removeUndefined(user));
            }
            
            set({ currentUser: user });
            return { success: true };
          } catch (error: any) {
            console.error('Google Sign-In failed:', error);
            if (error.code === 'auth/popup-closed-by-user') {
              return { success: false, error: 'Sign-in popup was closed before completing.' };
            }
            if (error.code === 'auth/configuration-not-found') {
              return { 
                success: false, 
                error: 'Google Sign-in is not enabled in Firebase Console. Please go to Firebase Console > Authentication > Sign-in method and enable Google.' 
              };
            }
            return { success: false, error: error.message || 'Google sign-in failed.' };
          }
        } else {
          // Offline fallback mock Google Login
          const email = 'google_test@sensorzone.lk';
          const users = get().registeredUsers;
          let user = users.find(u => u.email.toLowerCase() === email.toLowerCase());
          
          if (!user) {
            user = {
              id: email,
              name: 'Google Test User',
              email: email,
              phone: '',
              password: '',
              createdAt: new Date().toISOString(),
              addresses: []
            };
            set({ registeredUsers: [...users, user] });
          }
          
          set({ currentUser: user });
          return { success: true };
        }
      },

      logoutUser: () => {
        set({ currentUser: null });
      },

      updateProfile: async (name, phone, password) => {
        const { currentUser, registeredUsers } = get();
        if (!currentUser) return { success: false, error: 'No logged-in user found.' };

        const updatedUser: User = {
          ...currentUser,
          name,
          phone,
          ...(password ? { password } : {})
        };
        
        if (isFirebaseActive && db) {
          try {
            const sanitizedEmail = sanitizeLocalEmail(updatedUser.email);
            await dbSet(ref(db, `users/${sanitizedEmail}`), removeUndefined(updatedUser));
          } catch (error: any) {
            console.error('Update profile failed on Firebase:', error);
            return { success: false, error: 'Update profile failed: ' + error.message };
          }
        }

        const updatedUsers = registeredUsers.map(u => 
          u.id === currentUser.id ? updatedUser : u
        );

        set({
          currentUser: updatedUser,
          registeredUsers: updatedUsers
        });

        return { success: true };
      },

      addAddress: async (newAddr) => {
        const { currentUser, registeredUsers } = get();
        if (!currentUser) return;

        const address: Address = {
          id: 'addr-' + Math.floor(1000 + Math.random() * 9000),
          ...newAddr
        };

        const updatedUser: User = {
          ...currentUser,
          addresses: [...(currentUser.addresses || []), address]
        };

        if (isFirebaseActive && db) {
          try {
            const sanitizedEmail = sanitizeLocalEmail(updatedUser.email);
            await dbSet(ref(db, `users/${sanitizedEmail}`), removeUndefined(updatedUser));
          } catch (error: any) {
            console.error('Add address failed on Firebase:', error);
            return;
          }
        }

        const updatedUsers = registeredUsers.map(u => 
          u.id === currentUser.id ? updatedUser : u
        );

        set({
          currentUser: updatedUser,
          registeredUsers: updatedUsers
        });
      },

      updateAddress: async (addressId, updatedFields) => {
        const { currentUser, registeredUsers } = get();
        if (!currentUser) return;

        const updatedAddresses = (currentUser.addresses || []).map(addr => 
          addr.id === addressId ? { ...addr, ...updatedFields } : addr
        );

        const updatedUser: User = {
          ...currentUser,
          addresses: updatedAddresses
        };

        if (isFirebaseActive && db) {
          try {
            const sanitizedEmail = sanitizeLocalEmail(updatedUser.email);
            await dbSet(ref(db, `users/${sanitizedEmail}`), removeUndefined(updatedUser));
          } catch (error: any) {
            console.error('Update address failed on Firebase:', error);
            return;
          }
        }

        const updatedUsers = registeredUsers.map(u => 
          u.id === currentUser.id ? updatedUser : u
        );

        set({
          currentUser: updatedUser,
          registeredUsers: updatedUsers
        });
      },

      deleteAddress: async (addressId) => {
        const { currentUser, registeredUsers } = get();
        if (!currentUser) return;

        const updatedAddresses = (currentUser.addresses || []).filter(addr => addr.id !== addressId);

        const updatedUser: User = {
          ...currentUser,
          addresses: updatedAddresses
        };
        
        if (isFirebaseActive && db) {
          try {
            const sanitizedEmail = sanitizeLocalEmail(updatedUser.email);
            await dbSet(ref(db, `users/${sanitizedEmail}`), removeUndefined(updatedUser));
          } catch (error: any) {
            console.error('Delete address failed on Firebase:', error);
            return;
          }
        }

        const updatedUsers = registeredUsers.map(u => 
          u.id === currentUser.id ? updatedUser : u
        );

        set({
          currentUser: updatedUser,
          registeredUsers: updatedUsers
        });
      },

      deleteAccount: async () => {
        const { currentUser, registeredUsers } = get();
        if (!currentUser) return { success: false, error: 'No logged-in user found.' };

        if (isFirebaseActive && db) {
          try {
            const sanitizedEmail = sanitizeLocalEmail(currentUser.email);
            // Delete from Firebase
            await dbSet(ref(db, `users/${sanitizedEmail}`), null);
          } catch (error: any) {
            console.error('Delete account failed on Firebase:', error);
            return { success: false, error: 'Delete account failed: ' + error.message };
          }
        }

        const updatedUsers = registeredUsers.filter(u => u.id !== currentUser.id);

        set({
          currentUser: null,
          registeredUsers: updatedUsers
        });

        return { success: true };
      }
    }),
    {
      name: 'sensorzone-user-auth',
    }
  )
);

